import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { ColorPicker } from 'primereact/colorpicker';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useState, useEffect } from 'react';
import './estilo.css';
import { ProgressBar } from 'primereact/progressbar';

export const TablaEditableComponent = ({ data, onRowUpdate, readOnlyFields = [], coloresBase, activeIndex }) => {
    const [rows, setRows] = useState([]);
    const [savingIndexes, setSavingIndexes] = useState(new Set());
    const valoresToSigla = { 1: 'MUY_BAJO', 2: 'BAJO', 3: 'MEDIO', 4: 'ALTO', 5: 'MUY_ALTO' }

    const valoresConColores = Object.entries(valoresToSigla).map(([valor, sigla]) => {
        const colorItem = !coloresBase ? {} : coloresBase.find(c => c.sigla === sigla);
        return {
            valor: parseInt(valor),
            sigla: sigla,
            color: colorItem ? `#${colorItem.color_bg}` : '#ffffff',
        };
    });

    useEffect(() => {
        setRows(data || []);
    }, [data]);

    const addSavingIndex = (index) => {
        setSavingIndexes(prev => new Set(prev).add(index));
    };

    const removeSavingIndex = (index) => {
        setSavingIndexes(prev => {
            const updated = new Set(prev);
            updated.delete(index);
            return updated;
        });
    };

    // Maneja el evento de guardado de edición de fila
    const handleRowEditComplete = (e) => {
        const { newData, index } = e;
        const originalData = rows[index];
        const hasChanged = Object.keys(newData).some(key => newData[key] !== originalData[key]);
        if (!hasChanged) {
            return;
        }
        addSavingIndex(index);
        onRowUpdate?.(newData, index).then((response) => {
            const updated = [...rows];
            updated[index] = response?.data ?? newData;
            setRows(updated);
        }).catch((error) => {
            throw error;
        }).finally(() => {
            removeSavingIndex(index);
        });
    };

    const textEditor = (options) => (
        <InputText value={options.value} onChange={(e) => options.editorCallback(e.target.value)} />
    );

    const numberEditor = (options) => {
        // En el caso de querer extraer la cantidad de decimales del código "2-F-..."
        // const codigo = options.rowData?.codigo ?? '';
        // const match = codigo.match(/^(\d+)-F-/);
        // const decimals = match
        //     ? parseInt(match[1], 10)
        //     : 0;
        const codigo = options.rowData?.codigo ?? '';
        // Identificamos los códigos que son porcentajes
        const codigosPorcentaje = ['2-F-MONITOREO_INVERSION_MINIMA'];
        const isPercentage = codigosPorcentaje.includes(codigo); // Si es porcentaje, el máximo es 100
        const isCurrency = Boolean(codigo.match(/^(\d+)-F/)) && !isPercentage;

        return (
            <InputNumber value={options.value} onValueChange={e => options.editorCallback(e.value)} locale={isCurrency ? 'en-US' : undefined} mode={isCurrency ? 'currency' : 'decimal'} currency={isCurrency ? 'USD' : undefined} min={0}
                max={isPercentage ? 100 : undefined} suffix={isPercentage ? ' %' : ''} maxFractionDigits={isCurrency ? 2 : (isPercentage ? 2 : 0)} useGrouping={false} />
        );
    };

    const colorEditor = (options) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ColorPicker format="hex" value={options.value} onChange={(e) => options.editorCallback(e.value)} style={{ width: '3rem', height: '1.5rem', padding: 0, marginBottom: '7px' }} />
            <InputText value={options.value}
                onChange={(e) => {
                    let val = e.target.value.replace(/^#/, '');
                    if (/^[0-9A-Fa-f]{0,6}$/.test(val)) {
                        options.editorCallback(val);
                    }
                }}
                placeholder="hex"
                style={{ width: '5rem' }}
            />
        </div>
    );

    const columns = rows[0]
        ? Object.keys(rows[0]).filter(f => f !== 'id' && f !== 'codigo' && f !== 'sigla')
        : [];

    const rowEditorBody = (rowData, { rowEditor }) => {
        const editing = rowEditor.editing;
        const index = rows.findIndex(r => r.id === rowData.id);
        const isSaving = savingIndexes.has(index);

        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '4rem', position: 'relative' }}>
                {isSaving ? (
                    <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="4" />
                ) : (
                    <>
                        <Button icon="pi pi-pencil" className="p-button-rounded p-button-text icono-tabla-edit" onClick={rowEditor.onInitClick}
                            {...(!editing && { tooltip: 'Editar', tooltipOptions: { position: 'bottom' } })}
                            style={{ opacity: editing ? 0 : 1, pointerEvents: editing ? 'none' : 'auto', transition: 'opacity .1s' }}
                        />
                        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '1rem', opacity: editing ? 1 : 0, pointerEvents: editing ? 'auto' : 'none', transition: 'opacity .1s' }}>
                            <Button icon="pi pi-check" className="p-button-rounded p-button-text icono-tabla-edit" onClick={rowEditor.onSaveClick}
                                {...(editing && { tooltip: 'Guardar', tooltipOptions: { position: 'bottom' } })}
                            />
                            <Button icon="pi pi-times" className="p-button-rounded p-button-text icono-tabla-edit" onClick={rowEditor.onCancelClick}
                                {...(editing && { tooltip: 'Cancelar', tooltipOptions: { position: 'bottom' } })}
                            />
                        </div>
                    </>
                )}
            </div>
        );
    };

    const getContrastTextColor = (hex) => {
        const h = hex.replace(/^#/, '');
        const r = parseInt(h.substring(0, 2), 16);
        const g = parseInt(h.substring(2, 4), 16);
        const b = parseInt(h.substring(4, 6), 16);
        const lum = (0.299 * r + 0.587 * g + 0.114 * b);
        return lum > 186 ? '#000' : '#fff';
    };

    const formatCurrency = (value) => {
        return value?.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    };

    const cellBody = (field) => (rowData, { rowIndex }) => {
        const isSaving = savingIndexes.has(rowIndex);

        if (isSaving) {
            return (
                <div>
                    {/* <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="4" /> */}
                    <ProgressBar mode="indeterminate" style={{ width: '200px', height: '0.5rem' }} />
                </div>
            );
        }

        if (field === 'valor' && activeIndex === 0) {
            const esCurrency = ['2-F-MONITOREO_INVERSION_MENOR_A_MES'];
            const esPorcentaje = ['2-F-MONITOREO_INVERSION_MINIMA'];

            if (esCurrency.includes(rowData.codigo)) {
                return <span>{formatCurrency(rowData.valor)}</span>;
            } else if (esPorcentaje.includes(rowData.codigo)) {
                return <span>{rowData.valor} %</span>;
            } else {
                return <span>{rowData.valor}</span>;
            }
        }

        if (field === 'valor' && activeIndex === 1) {
            const colorSigla = valoresConColores.find(item => item.valor === rowData.valor)?.color || '#ffffff';

            return (
                <div style={{ display: 'flex', marginRight: '60px', minWidth: '100px' }}>
                    <span style={{ backgroundColor: colorSigla, color: getContrastTextColor(colorSigla), width: '2em', height: '2em', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', }} >
                        {rowData.valor}
                    </span>
                </div>
            );
        }

        // Render de color_bg
        if (field === 'color_bg') {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ backgroundColor: `#${rowData.color_bg}`, width: '2rem', height: '1.96rem', border: '1px solid #ccc', borderRadius: '3px', marginRight: '20px' }} />
                    <span>{rowData.color_bg}</span>
                </div>
            );
        }

        // Para las columas no especificadas se devuelve los datos simplemente
        return <span>{rowData[field]}</span>;
    };

    return (
        <>
            <DataTable value={rows} editMode="row" dataKey="id" onRowEditComplete={handleRowEditComplete} stripedRows
                pt={{ bodyRow: { style: { height: '5rem' } } }}>
                {columns.map((field) => (
                    <Column key={field} field={field} style={{ width: '300px' }}
                        header={field === 'color_bg' ? 'Color de Fondo' : field.charAt(0).toUpperCase() + field.slice(1)}
                        editor={readOnlyFields.includes(field) ? undefined : field === 'valor' ? numberEditor : field === 'color_bg' ? colorEditor : textEditor}
                        body={cellBody(field)}
                    />
                ))}
                <Column rowEditor body={rowEditorBody} headerStyle={{ width: '10rem' }} bodyStyle={{ textAlign: 'center' }} />
            </DataTable>
        </>
    );
};
